/**
 * Settings Page - Security / Password Management
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../shared/layout/Layout';
import { useLang } from '../../shared/i18n';
import { useAuth } from '../../shared/context/AuthContext';
import { setPassword, changePassword } from '../../services/authApi';
import { config } from '../../utils/config';
import './Settings.scss';

// Nav icons (same as InvoiceList)
const DashboardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);
const InvoiceIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);
const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const Settings: React.FC = () => {
    const { t } = useLang();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const hasPassword = user?.has_password ?? true;

    // Form state
    const [currentPwd, setCurrentPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const navItems = useMemo(() => [
        { id: 'rfq-invoices', label: t('navRfqInvoices'), icon: <DashboardIcon />, route: '/' },
        { id: 'invoices', label: t('navAllInvoices'), icon: <InvoiceIcon />, route: '/invoices' },
        { id: 'separator-1', label: '', icon: <></>, isSeparator: true },
        { id: 'settings', label: t('navSettings'), icon: <SettingsIcon />, route: '/settings' },
    ], [t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (newPwd.length < 8) {
            setMessage({ type: 'error', text: t('passwordMinLength') });
            return;
        }
        if (newPwd !== confirmPwd) {
            setMessage({ type: 'error', text: t('passwordMismatch') });
            return;
        }

        setLoading(true);
        try {
            const result = hasPassword
                ? await changePassword(currentPwd, newPwd)
                : await setPassword(newPwd);

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: hasPassword ? t('passwordChangeSuccess') : t('passwordSetSuccess'),
                });
                setCurrentPwd('');
                setNewPwd('');
                setConfirmPwd('');
            } else {
                setMessage({ type: 'error', text: result.error || t('passwordError') });
            }
        } catch {
            setMessage({ type: 'error', text: t('passwordError') });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout
            pageTitle={t('settingsTitle')}
            logo="Invoice Management"
            navItems={navItems}
            currentRoute={location.pathname}
            onNavigate={(route) => navigate(route)}
            user={{ name: user?.name || 'User', email: user?.email || '' }}
            onLogout={() => { logout(); window.location.href = config.PORTAL_URL; }}
        >
            <div className="settings-page">
                <div className="settings-card">
                    <h2 className="settings-section-title">
                        {hasPassword ? t('changePasswordSection') : t('setPasswordSection')}
                    </h2>
                    <p className="settings-section-desc">
                        {hasPassword ? t('changePasswordDesc') : t('setPasswordDesc')}
                    </p>

                    <form onSubmit={handleSubmit} className="settings-form">
                        {hasPassword && (
                            <div className="form-group">
                                <label>{t('currentPassword')}</label>
                                <input
                                    type="password"
                                    value={currentPwd}
                                    onChange={(e) => setCurrentPwd(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <label>{t('newPassword')}</label>
                            <input
                                type="password"
                                value={newPwd}
                                onChange={(e) => setNewPwd(e.target.value)}
                                required
                                minLength={8}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('confirmPassword')}</label>
                            <input
                                type="password"
                                value={confirmPwd}
                                onChange={(e) => setConfirmPwd(e.target.value)}
                                required
                                minLength={8}
                                autoComplete="new-password"
                            />
                        </div>

                        {message && (
                            <div className={`settings-message ${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        <button type="submit" className="settings-submit" disabled={loading}>
                            {loading
                                ? '...'
                                : hasPassword ? t('changePasswordButton') : t('setPasswordButton')}
                        </button>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default Settings;
